import {
  CalendarDays,
  FileClock,
  Heart,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Menu,
  Settings2,
  ShieldCheck,
  UserCheck,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { initials } from '../lib/format';

export function Brand({ inverse = false }: { inverse?: boolean }) {
  // Le logo est un monogramme trait fin noir sur fond transparent : sur les
  // panneaux sombres (pied de page, barre latérale admin), on sert la variante
  // "inverse" (traits blancs) générée à partir du même fichier, sinon il
  // devient invisible.
  const logo = inverse ? 'logo-principal-inverse' : 'logo-principal';
  return (
    <Link
      className={`brand ${inverse ? 'brand--inverse' : ''}`}
      to="/"
      aria-label="Orry et Rudy, accueil"
    >
      <picture className="brand__logo" aria-hidden="true">
        <source
          srcSet={`/media/${logo}-320.webp 1x, /media/${logo}-640.webp 2x`}
          type="image/webp"
        />
        <img src={`/media/${logo}-320.png`} alt="" width="48" height="48" loading="eager" />
      </picture>
      <span className="brand__text">
        <strong>Orry</strong>
        <i>&amp;</i>
        <strong>Rudy</strong>
      </span>
    </Link>
  );
}

export function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  return (
    <div className="public-shell">
      <header className="public-nav">
        <Brand />
        <button
          className="icon-button public-nav__toggle"
          type="button"
          aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((value) => !value)}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        <nav
          className={`public-nav__links ${menuOpen ? 'is-open' : ''}`}
          aria-label="Navigation principale"
        >
          <button
            className="public-nav__close"
            type="button"
            aria-label="Fermer le menu"
            onClick={() => setMenuOpen(false)}
          >
            <X aria-hidden="true" />
          </button>
          <NavLink to="/" end>
            Notre histoire
          </NavLink>
          <NavLink to="/galerie">Notre album photos</NavLink>
          <NavLink to="/confidentialite">Confidentialité</NavLink>
          <Link className="button button--primary button--small" to="/connexion">
            Mon invitation
          </Link>
        </nav>
      </header>
      <Outlet />
      <footer className="public-footer">
        <div className="public-footer__top">
          <Brand inverse />
          <p>Un jour, une promesse, toute une vie.</p>
          <div className="public-footer__links">
            <Link to="/confidentialite">Confidentialité</Link>
            <Link to="/admin/connexion">Administration</Link>
            <Link to="/accueil/connexion">Accueil événement</Link>
          </div>
        </div>
        <small className="public-footer__copyright">
          © {new Date().getFullYear()} Orry &amp; Rudy
        </small>
      </footer>
    </div>
  );
}

const adminLinks = [
  { to: '/admin', end: true, label: "Vue d'ensemble", icon: LayoutDashboard },
  { to: '/admin/mariage', label: 'Notre mariage', icon: Heart },
  { to: '/admin/ceremonies', label: 'Cérémonies', icon: CalendarDays },
  { to: '/admin/invites', label: 'Invités', icon: Users },
  { to: '/admin/agents', label: 'Agents', icon: UserCog },
  { to: '/admin/tables', label: 'Tables & tenue', icon: LayoutGrid },
  { to: '/admin/audit', label: "Journal d'audit", icon: FileClock },
];

export function AdminLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  useEffect(() => setOpen(false), [location.pathname]);

  const logout = async () => {
    await auth.signOut();
    navigate('/admin/connexion', { replace: true });
  };

  return (
    <div className="admin-shell">
      <button
        className="admin-mobile-toggle"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir la navigation"
      >
        <Menu aria-hidden="true" />
        <span>Menu</span>
      </button>
      {open && (
        <button
          className="sidebar-backdrop"
          aria-label="Fermer la navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        className={`admin-sidebar ${open ? 'is-open' : ''}`}
        aria-label="Navigation administration"
      >
        <div className="admin-sidebar__top">
          <Brand inverse />
          <button
            className="icon-button admin-sidebar__close"
            type="button"
            aria-label="Fermer"
            onClick={() => setOpen(false)}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="admin-sidebar__context">
          <span className="admin-sidebar__avatar">
            {initials(auth.session?.principal.displayName)}
          </span>
          <div>
            <strong>Espace des mariés</strong>
            <small>Administration</small>
          </div>
        </div>
        <nav className="admin-sidebar__nav">
          {adminLinks.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}>
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar__bottom">
          {auth.hasRole('agent') && (
            <Link to="/accueil/ceremonies">
              <UserCheck aria-hidden="true" />
              Mode accueil
            </Link>
          )}
          <button type="button" onClick={() => void logout()}>
            <LogOut aria-hidden="true" />
            Se déconnecter
          </button>
        </div>
      </aside>
      <main id="contenu" className="admin-main">
        <div className="admin-topbar">
          <div>
            <span className="live-dot" /> Mariage Orry &amp; Rudy
          </div>
          <span className="secure-label">
            <ShieldCheck aria-hidden="true" /> Espace sécurisé
          </span>
        </div>
        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function GuestLayout() {
  const navigate = useNavigate();
  const auth = useAuth();
  const logout = async () => {
    await auth.signOut();
    navigate('/', { replace: true });
  };
  return (
    <div className="guest-shell">
      <header className="guest-nav">
        <Brand />
        <button
          className="button button--ghost button--small"
          type="button"
          onClick={() => void logout()}
        >
          <LogOut size={17} aria-hidden="true" /> Déconnexion
        </button>
      </header>
      <main id="contenu" className="guest-main">
        <Outlet />
      </main>
      <footer className="guest-footer">
        Votre invitation est personnelle. À l'accueil, donnez votre nom ou votre numéro de
        téléphone.
      </footer>
    </div>
  );
}

export function CheckinLayout() {
  const navigate = useNavigate();
  const auth = useAuth();
  const logout = async () => {
    sessionStorage.removeItem('checkin.ceremony');
    await auth.signOut();
    navigate('/accueil/connexion', { replace: true });
  };

  return (
    <div className="checkin-shell">
      <header className="checkin-header">
        <Link className="checkin-brand" to="/accueil/ceremonies">
          <UserCheck aria-hidden="true" /> Contrôle des accès
        </Link>
        <div>
          {auth.hasRole('admin') && (
            <Link className="button button--ghost button--small" to="/admin">
              <Settings2 size={17} /> Administration
            </Link>
          )}
          <button
            className="icon-button"
            type="button"
            onClick={() => void logout()}
            aria-label="Se déconnecter"
          >
            <LogOut aria-hidden="true" />
          </button>
        </div>
      </header>
      <main id="contenu" className="checkin-main">
        <Outlet />
      </main>
    </div>
  );
}
